import React, { useEffect, useRef, useMemo } from "react";
import { profileData } from "../../data/mock";
import { ArrowDown, MapPin, Terminal, Database, Zap, FileDown } from "lucide-react";

/* Floating binary data particles */
const DataParticles = () => {
  const particles = useMemo(() => {
    const items = [];
    const chars = ["01", "10", "0x", "SELECT", "JOIN", "DDL", "ETL", "NULL", "INT", ">>", "=>", "{ }", "[ ]"];
    for (let i = 0; i < 18; i++) {
      items.push({
        id: i,
        char: chars[i % chars.length],
        left: `${5 + Math.random() * 90}%`,
        delay: `${Math.random() * 10}s`,
        duration: `${12 + Math.random() * 10}s`,
        size: `${9 + Math.random() * 3}px`,
      });
    }
    return items;
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {particles.map((p) => (
        <span
          key={p.id}
          className="absolute font-['Fira_Code'] text-[#06B6D4]/[0.07] select-none"
          style={{
            left: p.left,
            fontSize: p.size,
            animation: `dataStream ${p.duration} linear ${p.delay} infinite`,
          }}
        >
          {p.char}
        </span>
      ))}
    </div>
  );
};

/* Animated pipeline SVG decoration */
const PipelineViz = () => (
  <svg
    className="absolute bottom-8 left-0 right-0 w-full h-16 opacity-[0.06] pointer-events-none"
    viewBox="0 0 1200 60"
    fill="none"
  >
    <line x1="0" y1="30" x2="300" y2="30" stroke="#06B6D4" strokeWidth="1" strokeDasharray="6 4" />
    <circle cx="300" cy="30" r="5" fill="#06B6D4" />
    <line x1="310" y1="30" x2="600" y2="30" stroke="#06B6D4" strokeWidth="1" strokeDasharray="6 4" />
    <circle cx="600" cy="30" r="5" fill="#06B6D4" />
    <line x1="610" y1="30" x2="900" y2="30" stroke="#06B6D4" strokeWidth="1" strokeDasharray="6 4" />
    <circle cx="900" cy="30" r="5" fill="#06B6D4" />
    <line x1="910" y1="30" x2="1200" y2="30" stroke="#06B6D4" strokeWidth="1" strokeDasharray="6 4" />
  </svg>
);

const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!heroRef.current) return;
      const glow = heroRef.current.querySelector(".hero-glow");
      if (glow) {
        glow.style.background = `radial-gradient(500px circle at ${e.clientX}px ${e.clientY}px, rgba(6, 182, 212, 0.04), transparent 60%)`;
      }
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden scanline-overlay"
    >
      <div className="hero-glow absolute inset-0 pointer-events-none z-0" />
      <DataParticles />

      {/* Ambient orbs */}
      <div className="absolute top-20 right-[20%] w-[500px] h-[500px] rounded-full bg-[#06B6D4]/[0.03] blur-[120px] pointer-events-none" />
      <div className="absolute bottom-20 left-[10%] w-[400px] h-[400px] rounded-full bg-[#F59E0B]/[0.02] blur-[100px] pointer-events-none" />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-[1fr_auto] gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* Terminal-style header */}
            <div className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0A1628] border border-[#1E293B] mb-8">
              <Terminal size={13} className="text-[#06B6D4]" />
              <span className="text-[11px] text-[#06B6D4] font-['Fira_Code'] tracking-widest">
                ~/data-engineer
              </span>
              <span className="text-[11px] text-[#475569] font-['Fira_Code']">$</span>
              <span className="text-[11px] text-emerald-400 font-['Fira_Code']">status</span>
              <span className="w-[1px] h-3.5 bg-emerald-400 animate-[terminalBlink_1s_infinite]" />
            </div>

            {/* Name */}
            <h1 className="animate-fade-in-up delay-100 font-['Figtree'] text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.95] mb-5">
              <span className="text-[#F1F5F9]">{profileData.name.split(" ")[0]}</span>
              <br />
              <span className="bg-gradient-to-r from-[#06B6D4] to-[#0891B2] bg-clip-text text-transparent">
                {profileData.name.split(" ")[1]}
              </span>
            </h1>

            {/* Role badge */}
            <div className="animate-fade-in-up delay-200 flex items-center gap-3 mb-5">
              <div className="flex items-center gap-1.5">
                <Database size={14} className="text-[#06B6D4]" />
                <span className="font-['Fira_Code'] text-xs text-[#06B6D4] tracking-wider uppercase">
                  {profileData.title}
                </span>
              </div>
              <div className="h-3 w-[1px] bg-[#1E293B]" />
              <div className="flex items-center gap-1 text-[11px] text-[#475569]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Open to opportunities
              </div>
            </div>

            {/* Tagline */}
            <p className="animate-fade-in-up delay-300 text-base md:text-lg text-[#64748B] max-w-md mb-3 leading-relaxed">
              {profileData.tagline}
            </p>

            {/* Location */}
            <div className="animate-fade-in-up delay-300 flex items-center gap-1.5 text-[#475569] text-xs mb-8">
              <MapPin size={12} />
              <span className="font-['Fira_Code']">{profileData.location}</span>
            </div>

            {/* CTA buttons */}
            <div className="animate-fade-in-up delay-400 flex flex-col sm:flex-row gap-4 mb-14">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-primary"
              >
                <Zap size={16} />
                Initialize Contact
              </a>
              <a
                href={profileData.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="btn-outline group"
              >
                <FileDown size={15} className="group-hover:animate-bounce" />
                Download Resume
              </a>
              <a
                href="#experience"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#experience")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-outline"
              >
                <Database size={15} />
                Explore Pipeline
              </a>
            </div>

            {/* Stats row */}
            <div className="animate-fade-in-up delay-500 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {profileData.stats.map((stat, i) => (
                <div
                  key={i}
                  className="p-3 rounded-lg bg-[#0A1628]/60 border border-[#1E293B]/50 group hover:border-[#06B6D4]/20 transition-all duration-300"
                >
                  <div className="text-xl font-['Figtree'] font-bold text-[#F1F5F9] group-hover:text-[#06B6D4] transition-colors">
                    {stat.value}
                  </div>
                  <div className="text-[10px] text-[#475569] font-['Fira_Code'] tracking-wide mt-0.5">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Profile Photo */}
          <div className="animate-fade-in-right delay-300 hidden lg:block">
            <div className="profile-photo-container relative">
              <div className="profile-photo-glow" />
              {/* Decorative frame */}
              <div className="relative">
                {/* Corner brackets */}
                <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-[#06B6D4]/40 rounded-tl-sm" />
                <div className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-[#06B6D4]/40 rounded-tr-sm" />
                <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-[#06B6D4]/40 rounded-bl-sm" />
                <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-[#06B6D4]/40 rounded-br-sm" />

                <img
                  src={profileData.photo}
                  alt={profileData.name}
                  className="profile-photo w-[320px] h-[380px] object-cover object-top"
                />

                {/* Dark gradient overlay to blend white bg */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#06080D] via-transparent to-[#06080D]/40 pointer-events-none" />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-[#06080D]/50 via-transparent to-[#06080D]/50 pointer-events-none" />

                {/* Overlay data label */}
                <div className="absolute bottom-4 left-4 right-4 px-3 py-2 rounded-md bg-[#06080D]/80 backdrop-blur-sm border border-[#1E293B]">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#06B6D4] animate-pulse" />
                    <span className="text-[10px] font-['Fira_Code'] text-[#06B6D4]">
                      DATA_ENGINEER.status = "active"
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating nodes */}
              <div className="absolute -top-4 -right-4 data-node" style={{ animationDelay: "0s" }} />
              <div className="absolute top-1/3 -right-6 data-node" style={{ animationDelay: "0.7s" }} />
              <div className="absolute bottom-1/4 -left-5 data-node" style={{ animationDelay: "1.4s" }} />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="animate-fade-in-up delay-800 mt-10 flex flex-col items-center gap-2">
          <span className="text-[9px] text-[#334155] tracking-[4px] uppercase font-['Fira_Code']">
            scroll.down()
          </span>
          <ArrowDown size={14} className="text-[#334155] animate-bounce" />
        </div>
      </div>

      <PipelineViz />
    </section>
  );
};

export default HeroSection;
